import React from 'react';
import { bool, element, node, func, oneOf, string } from 'prop-types';
import Icon from '../../Icon';
import * as css from './button.css';
import { dt } from '../../../helpers/Utils/GetTailwindToken';
import withPerformanceMeasurement from '../../../hoc/PerformanceObserver/HOCPerfMeasure';

const configButton = {
    length: {
        Default: '40px',
        Md: '40px',
        Lg: '48px',
    },
    classes: {
        defaults: dt(['buttonDefault', 'font-open-sans-semibold']),
        isDisabled: dt([
            'buttonDisabled',
            'font-open-sans-semibold',
            '!cursor-not-allowed',
            'bg-opacity-50',
        ]),
        font: {
            Default: dt(['text-normal']),
            Md: dt(['text-normal']),
            Sm: dt(['text-small']),
            Xs: dt(['text-xsmall']),
            Mini: dt(['text-sm', 'leading-4', '!p-[7px]', '!min-w-[110px]']),
            Lg: dt(['text-xlarge']),
        },
        Primary: {
            theme: dt(['text-white', 'p-2']),
            enabled: dt(['bg-brand-red', 'hover:bg-error-border', 'focus:bg-error-border']),
            loading: dt(['bg-error-border', 'cursor-not-allowed']),
        },
        PrimaryAlt: {
            theme: dt(['text-brand-red', 'border', 'border-solid', 'border-brand-red', 'p-[7px]']),
            enabled: dt([
                'bg-white',
                'hover:text-error-border',
                'border',
                'border-solid',
                'border-brand-red',
                'hover:border-error-border',
                'focus:border-error-border',
            ]),
            loading: dt([
                'bg-white',
                'border-1',
                'border-solid',
                'border-error-border',
                'cursor-not-allowed',
            ]),
        },
        Secondary: {
            theme: dt(['text-black', 'border', 'border-solid', 'border-gray-40', 'p-[7px]']),
            enabled: dt(['bg-white', 'hover:bg-light-white', 'focus:bg-light-white']),
            loading: dt([
                'bg-light-white',
                'border-1',
                'border-solid',
                'border-gray-40',
                'cursor-not-allowed',
            ]),
        },
        Tertiary: {
            theme: dt(['text-white', 'border-solid', 'border-1', 'border-gray-40', 'p-2']),
            enabled: dt(['bg-gray-70', 'hover:bg-black', 'focus:bg-black']),
            loading: dt(['bg-black', 'cursor-not-allowed']),
        },
        Text: {
            theme: dt(['textButton', 'underline', 'py-2', 'px-0']),
            enabled: '',
            loading: '',
            disabled: dt(['border-0', '!bg-transparent']),
        },
        Link: {
            theme: dt([
                'font-normal',
                'font-open-sans-regular',
                'underline',
                '!min-h-[0]',
                '!p-0',
                'w-auto',
                'inline-block',
            ]),
            enabled: '',
            loading: '',
            disabled: dt([
                'border-0',
                '!bg-transparent',
                '!min-h-0',
                'h-auto',
                'w-auto',
                'p-0',
                'inline-block',
                'font-normal',
            ]),
        },
    },
};
const configIcon = {
    length: {
        Default: '20px',
        Md: '20px',
        Lg: '24px',
    },
    classes: 'mr-1',
};
const configSpinner = {
    length: {
        Default: '20px',
        Md: '20px',
        Lg: '24px',
    },
    classes: {
        defaults: `${css.spinner} relative rounded-full`,
        Primary: `${css['spinner-white-100']} ${css['after:bg-error-border']}`,
        PrimaryAlt: `${css['spinner-brand-red']} ${css['after:bg-white']}`,
        Secondary: `${css['spinner-gray-600']} ${css['after:bg-light-white']}`,
        Tertiary: `${css['spinner-white-100']} ${css['after:bg-black']}`,
    },
};

const validateState = ({ loading, isDisabled, linkUrl }) => {
    let stateProp;
    let isInvalid = null;

    if (loading) stateProp = 'loading';
    else if (isDisabled) stateProp = 'isDisabled';

    if (linkUrl && stateProp) {
        isInvalid = new Error(
            ['An `href` cannot be used in combination with `', stateProp, '` in Button.'].join('')
        );
    }

    return isInvalid;
};

/**
 * Button component to standardize the styling and html structure of any button.
 */
const Button = (props) => {
    const {
        automationId,
        children,
        className,
        isDisabled,
        linkUrl,
        icon,
        loading,
        size,
        type,
        iconComponent,
        buttonType,
        onClick,
        onMouseDown,
        onBlur,
        ellipsis,
        buttonId,
        ...attrs
    } = props;

    const commonAttrs = {
        'data-automation-id': automationId,
        onBlur,
        onClick,
        onMouseDown,
        id: buttonId,
        disabled: isDisabled,
        style: {
            minHeight: configButton.length[size],
        },
        ...attrs,
    };

    const linkAttrs = {
        href: !isDisabled ? linkUrl : null,
        ...commonAttrs,
    };

    const buttonAttrs = {
        type,
        isDisabled,
        ...commonAttrs,
    };

    const getClasses = () => {
        // We need to remove if (!buttonType) return; after migration
        if (!buttonType) return;
        const configClasses = configButton.classes;
        const presetConfig = configClasses[buttonType];
        const classes = [configClasses.defaults, configClasses.font[size]];

        if (className) classes.push(className);

        if (isDisabled) classes.push(configClasses.isDisabled, presetConfig.disabled);
        else if (loading) classes.push(presetConfig.loading, presetConfig.theme);
        else classes.push(presetConfig.enabled, presetConfig.theme);

        return classes.join(' ');
    };

    const getIcon = () => {
        if (!icon) return null;

        return (
            <Icon
                className={configIcon.classes}
                height={configIcon.length[size]}
                width={configIcon.length[size]}
            >
                {iconComponent}
            </Icon>
        );
    };

    const getSpinner = () => {
        // if (!loading) return null;
        // We need to remove || !buttonType after migration
        if (!loading || !buttonType) return null;

        const classes = `${configSpinner.classes.defaults} ${configSpinner.classes[buttonType]}`;

        return (
            <div
                aria-label="Loading..."
                className={classes}
                style={{
                    height: configSpinner.length[size],
                    width: configSpinner.length[size],
                }}
            />
        );
    };

    const getContent = () => (
        <span className={dt(['flex', 'items-center', 'justify-center'])}>
            {getSpinner()}
            {getIcon()}
            {children}
        </span>
    );

    if (validateState(props, 'linkUrl', 'Button')) return null;

    if (linkUrl)
        return (
            // eslint-disable-next-line react/jsx-props-no-spreading
            <a className={getClasses()} {...linkAttrs}>
                {getContent()}
            </a>
        );

    return (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <button id={buttonId} type={type} className={getClasses()} {...buttonAttrs}>
            {getContent()}
        </button>
    );
};

Button.propTypes = {
    onClick: func,
    onMouseDown: func,
    onBlur: func,
    children: node,
    ellipsis: bool,
    /** Automation ID */
    automationId: string,
    /** The text or node(s) to render in the component. */
    children: node,
    /** Additional classes to add to button */
    className: string,
    /** Disables the button when true */
    isDisabled: bool,
    /** URL to use for the `href` value on an `<a/>` element */
    linkUrl: validateState,
    /** Icon to use on button, such as 'ydt-lock' */
    icon: string,
    /** Icon to use on button, such as 'ydt-lock' */
    iconComponent: element,
    /** Displays loading animation when true */
    loading: bool,
    /** Button theme */
    buttonType: oneOf([
        'Default',
        'Primary',
        'PrimaryAlt',
        'Secondary',
        'Tertiary',
        'Text',
        'Link',
    ]),
    /** Button size */
    size: oneOf(['Default', 'Xl', 'Lg', 'Md', 'Sm', 'Xs', 'Mini']),
    /** Button `type` attribute */
    type: oneOf(['button', 'submit', 'linkbutton']),
    /** Button Id* */
    buttonId: string,
};

Button.defaultProps = {
    automationId: null,
    children: null,
    className: null,
    linkUrl: null,
    icon: null,
    loading: false,
    buttonType: 'Primary',
    size: 'Default',
    type: 'button',
    iconComponent: <></>,
    buttonId: null,
    onClick: null,
    onMouseDown: null,
    isDisabled: false,
    onBlur: null,
    ellipsis: false,
};

export default withPerformanceMeasurement(Button);
